import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Card, Input } from "@velocity-ehs/component-library";
import FormSkeleton from "../../../sharedComponents/FormSkeleton";
import { Person } from "../Person";
import classes from "./ContactInformation.module.scss";
import { useEffect } from "react";
import { Mode } from "../PersonForm";
import { emailRegExp } from "../../../supportFunctions/ValidationHelpers";

type ContactInformationProps = {
  formMethods: UseFormReturn<Person, unknown>;
  isLoading: boolean;
  readOnly?: boolean;
  isSensitiveDataReadOnly: boolean;
  isUser: boolean;
  mode: Mode;
};

const ContactInformation = ({
  formMethods,
  isLoading,
  readOnly,
  isSensitiveDataReadOnly,
  isUser,
  mode,
}: ContactInformationProps): React.ReactElement => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    watch,
    clearErrors,
    getFieldState,
  } = formMethods;

  const streetAddressRules = {
    required: t("person_Form_Street_Address_Required"),
  };
  const zipCodeRules = {
    required: t("person_Form_Zip_Code_Required"),
  };
  const cityRules = {
    required: t("person_Form_City_Required"),
  };
  const stateProvinceRules = {
    required: t("person_Form_State_Prov_Required"),
  };
  const countryRules = {
    required: t("person_Form_Country_Required"),
  };

  const streetAddressValue = watch("streetAddress");
  const streetAddress2Value = watch("address2");
  const zipPostalValue = watch("zipPostal");
  const cityValue = watch("city");
  const stateProvValue = watch("stateProv");
  const countryValue = watch("country");

  const streetAddressRulesValidation =
    streetAddress2Value ||
    zipPostalValue ||
    cityValue ||
    stateProvValue ||
    countryValue
      ? streetAddressRules
      : { required: false };
  const zipCodeRulesValidation =
    streetAddressValue || cityValue || stateProvValue || countryValue
      ? zipCodeRules
      : { required: false };
  const cityRulesValidation =
    streetAddressValue || zipPostalValue || stateProvValue || countryValue
      ? cityRules
      : { required: false };
  const stateProvinceValidation =
    streetAddressValue || zipPostalValue || cityValue || countryValue
      ? stateProvinceRules
      : { required: false };
  const countryValidation =
    streetAddressValue || zipPostalValue || cityValue || stateProvValue
      ? countryRules
      : { required: false };

  const clearStaleError = (
    validationRule: { required: boolean | string },
    fieldName: "streetAddress" | "zipPostal" | "city" | "stateProv" | "country"
  ) => {
    if (!validationRule.required && getFieldState(fieldName).error) {
      clearErrors(fieldName);
    }
  };
  useEffect(() => {
    clearStaleError(streetAddressRulesValidation, "streetAddress");
    clearStaleError(zipCodeRulesValidation, "zipPostal");
    clearStaleError(cityRulesValidation, "city");
    clearStaleError(stateProvinceValidation, "stateProv");
    clearStaleError(countryValidation, "country");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    streetAddressValue,
    streetAddress2Value,
    zipPostalValue,
    cityValue,
    stateProvValue,
    countryValue,
  ]);

  return (
    <Card
      title={t("contact_information_card_title")}
      style={{ paddingBottom: "2px" }}
    >
      {isLoading ? (
        <FormSkeleton rows={5} />
      ) : (
        <>
          <Input
            readOnly={readOnly}
            control={control}
            name="phone"
            label={t("universal_Phone")}
            rules={{
              maxLength: {
                value: 100,
                message: t("max_length_100_char_form_validation"),
              },
            }}
            errorMessage={errors && errors.phone?.message}
          />
          {mode === Mode.Add || !isUser ? (
            <Input
              readOnly={isSensitiveDataReadOnly}
              control={control}
              name="email"
              label={t("universal_Email")}
              rules={{
                maxLength: {
                  value: 100,
                  message: t("max_length_100_char_form_validation"),
                },
                pattern: {
                  value: emailRegExp,
                  message: t("message_invalid_email_address"),
                },
              }}
              errorMessage={errors && errors.email?.message}
            />
          ) : null}
          <Input
            control={control}
            readOnly={readOnly}
            name="faxNumber"
            label={t("universal_fax_number")}
            rules={{
              maxLength: {
                value: 15,
                message: t("max_length_15_char_form_validation"),
              },
            }}
            errorMessage={errors && errors.faxNumber?.message}
          />
          <p className={classes.addressText}>{t("addess_information_text")}</p>
          <Input
            control={control}
            name="streetAddress"
            label={t("universal_Street_Address")}
            errorMessage={errors ? errors.streetAddress?.message : undefined}
            rules={streetAddressRulesValidation}
            readOnly={isSensitiveDataReadOnly}
          />
          <Input
            control={control}
            name="address2"
            label={`${t("universal_Street_Address")} 2`}
            errorMessage={errors ? errors.address2?.message : undefined}
            readOnly={isSensitiveDataReadOnly}
          />
          <Input
            control={control}
            name="city"
            label={t("universal_City")}
            errorMessage={errors ? errors.city?.message : undefined}
            rules={cityRulesValidation}
            readOnly={isSensitiveDataReadOnly}
          />
          <Input
            control={control}
            name="zipPostal"
            label={t("universal_Zip_Code")}
            errorMessage={errors ? errors.zipPostal?.message : undefined}
            rules={zipCodeRulesValidation}
            readOnly={isSensitiveDataReadOnly}
          />
          <Input
            control={control}
            name="stateProv"
            label={t("universal_State_Province")}
            errorMessage={errors ? errors.stateProv?.message : undefined}
            rules={stateProvinceValidation}
            readOnly={isSensitiveDataReadOnly}
          />
          <Input
            control={control}
            name="country"
            label={t("universal_country")}
            errorMessage={errors ? errors.country?.message : undefined}
            rules={countryValidation}
            readOnly={isSensitiveDataReadOnly}
          />
        </>
      )}
    </Card>
  );
};

export { ContactInformation };
